import { useState, useEffect } from 'react';
import { assessmentData } from './data/assessmentData';
import Introduction from './components/Introduction';
import AssessmentSection from './components/AssessmentSection';
import Results from './components/Results';
import ProgressBar from './components/ProgressBar';

function sendHeightUpdate() {
  const contentHeight = document.getElementById('root').getBoundingClientRect().height;
  const height = contentHeight + 100;
    
  window.parent.postMessage({ type: "iframeHeight", height }, "*");
}

function App() {
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isComplete, setIsComplete] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({
    companyName: '',
    email: '',
    assessmentDate: new Date().toISOString().split('T')[0],
    displayDate: new Date().toLocaleDateString()
  });
  const [hasSetResultsHeight, setHasSetResultsHeight] = useState(false);

  useEffect(() => {
    let timeoutId;
    const debounce = (func, wait) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(func, wait);
    };

    if (isComplete && !hasSetResultsHeight) {
      const finalTimeoutId = setTimeout(() => {
        sendHeightUpdate();
        setHasSetResultsHeight(true);
      }, 500);
      
      return () => clearTimeout(finalTimeoutId);
    }

    if (!isComplete) {
      sendHeightUpdate();
      
      const secondTimeoutId = setTimeout(sendHeightUpdate, 500);
      
      const resizeObserver = new ResizeObserver(() => {
        debounce(sendHeightUpdate, 100);
      });

      const rootElement = document.getElementById('root');
      if (rootElement) {
        resizeObserver.observe(rootElement);
      }

      return () => {
        resizeObserver.disconnect();
        clearTimeout(timeoutId);
        clearTimeout(secondTimeoutId);
      };
    }
  }, [currentSection, isComplete, answers, hasSetResultsHeight]);

  const handleAnswer = (sectionId, questionIndex, value) => {
    setAnswers(prev => ({
      ...prev,
      [`${sectionId}-${questionIndex}`]: value
    }));
  };

  const handleStart = (info) => {
    if (info?.testAnswers) {
      // If test answers are provided, set them and go directly to results
      setAnswers(info.testAnswers);
      if (info.companyName || info.email) {
        setCompanyInfo({
          ...companyInfo,
          companyName: info.companyName || '',
          email: info.email || ''
        });
      }
      setIsComplete(true);
    } else {
      // Start assessment without company info
      setCurrentSection(1);
    }
  };

  const handleNext = () => {
    if (currentSection < assessmentData.sections.length) {
      setCurrentSection(prev => prev + 1);
    } else {
      // If we've completed all sections, go to results
      setIsComplete(true);
    }
  };

  const handlePrevious = () => {
    if (currentSection > 0) {
      setCurrentSection(prev => prev - 1);
    }
  };

  // Extract URL parameters for company info if available
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const company = urlParams.get('company');
    const email = urlParams.get('email');
    
    if (company || email) {
      setCompanyInfo(prev => ({
        ...prev,
        companyName: company || prev.companyName,
        email: email || prev.email
      }));
    }
  }, []);

  if (isComplete) {
    return (
      <div>
        <Results answers={answers} companyInfo={companyInfo} />
      </div>
    );
  }

  return (
    <div className="py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {currentSection > 0 && (
          <ProgressBar 
            currentSection={currentSection} 
            totalSections={assessmentData.sections.length} 
          />
        )}

        {currentSection === 0 ? (
          <Introduction 
            text={assessmentData.introduction}
            onStart={handleStart}
          />
        ) : (
          <AssessmentSection
            section={assessmentData.sections[currentSection - 1]}
            answers={answers}
            onAnswer={handleAnswer}
            onNext={handleNext}
            onPrevious={handlePrevious}
            isLastSection={currentSection === assessmentData.sections.length}
            onSubmit={() => setIsComplete(true)}
          />
        )}
      </div>
    </div>
  );
}

export default App; 